// Base API URL
export const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
const API_BASE_URL = `${BASE_URL}/api/v2`;

// Auth Endpoints
export const AUTH_URLS = {
  SEND_VERIFICATION_CODE: `${API_BASE_URL}/auth/email_verification`,
  SIGN_UP: `${API_BASE_URL}/auth/signup`,
  INVITATION_SIGN_UP: `${API_BASE_URL}/auth/signup/invited`,
  LOGIN: `${API_BASE_URL}/auth/login`,
  REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh`,
  FORGET_PASSWORD: `${API_BASE_URL}/auth/forget_password`,
  RESET_PASSWORD: `${API_BASE_URL}/auth/reset`
};

// Home Endpoints
export const HOME_URLS = {
  GET: `${API_BASE_URL}/home`
};

// Account Endpoints
export const ACCOUNT_URLS = {
  GET: `${API_BASE_URL}/account`,
  UPDATE_PROFILE: `${API_BASE_URL}/account`,
  GET_INVITATION: `${API_BASE_URL}/account/invitation`,
  UPGRADE_REQUEST: `${API_BASE_URL}/account/upgrade_request`
};

// Agency Endpoints
export const AGENCY_URLS = {
  UPDATE_PROFILE: `${API_BASE_URL}/agency`,
  UPLOAD_LOGO: `${API_BASE_URL}/agency/logo`,
  GET_CUSTOM_FEATURES: `${API_BASE_URL}/agency/features`
};

// Team Endpoints
export const TEAM_URLS = {
  GET_ALL: `${API_BASE_URL}/team`,
  REMOVE_MEMBER: `${API_BASE_URL}/team/member`,
  UPDATE_ROLE: `${API_BASE_URL}/team/role`,
  INVITE: `${API_BASE_URL}/team/invite`,
  REMOVE_INVITATION: `${API_BASE_URL}/team/invite`
};

// Client Endpoints
export const CLIENT_URLS = {
  GET_ALL: `${API_BASE_URL}/client`,
  CREATE: `${API_BASE_URL}/client`,
  GET: (clientId: string) => `${API_BASE_URL}/client/${clientId}`,
  UPDATE: (clientId: string) => `${API_BASE_URL}/client/${clientId}`,
  ARCHIVE: (clientId: string) => `${API_BASE_URL}/client/${clientId}/archive`
};

// Job Endpoints
export const JOB_URLS = {
  EXTRACT_JD: `${API_BASE_URL}/jd/extract`,
  ANALYZE_REQUIREMENTS: `${API_BASE_URL}/jd/requirements`,
  GET_SUMMARY: `${API_BASE_URL}/job_summary`,
  GET_ALL: `${API_BASE_URL}/job`,
  CREATE: `${API_BASE_URL}/job`,
  GET_PUBLISH_JOB_BOARD: `${API_BASE_URL}/job/publish_job_board`,
  GET_CATEGORIES: `${API_BASE_URL}/job/categories`,
  UPLOAD_JD: (jobId: string) => `${API_BASE_URL}/job/${jobId}/file`,
  GET_DETAILS: (jobId: string) => `${API_BASE_URL}/job/${jobId}`,
  UPDATE: (jobId: string) => `${API_BASE_URL}/job/${jobId}`,
  ADD_CANDIDATE: (jobId: string) => `${API_BASE_URL}/job/${jobId}/add_candidate`,
  UPDATE_STATUS: (jobId: string) => `${API_BASE_URL}/job/${jobId}/status`,
  UPDATE_PUBLISH_STATUS: (jobId: string) => `${API_BASE_URL}/job/${jobId}/publish_job_board`,
  UPDATE_APPLICATION_QUESTIONS: (jobId: string) =>
    `${API_BASE_URL}/job/${jobId}/application_questions`,
  GET_MATCH_CANDIDATES: (jobId: string) => `${API_BASE_URL}/job/${jobId}/match_candidates`,
  GET_AVAILABLE_CANDIDATES: (jobId: string) => `${API_BASE_URL}/job/${jobId}/available_candidates`
};

// Job Candidate Endpoints
export const JOB_CANDIDATE_URLS = {
  GET_ALL: (jobId: string) => `${API_BASE_URL}/job/${jobId}/candidate`,
  CREATE: (jobId: string) => `${API_BASE_URL}/job/${jobId}/candidate`,
  CREATE_FROM_VETERANS_RESUME: (jobId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/veterans_resume`,
  GET_DETAILS: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}`,
  DELETE: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}`,
  GET_PREVIOUS_AND_NEXT_CANDIDATES: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/previous_and_next`,
  UPDATE_PROFILE: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}`,
  UPDATE_STATUS: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/status`,
  UPDATE_SCORE: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/score`,
  UPDATE_NOTES: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/notes`,
  UPDATE_FOLLOWUP_QUESTIONS: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/followup_questions`,
  CREATE_MEETING: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/meeting`,
  CREATE_MOCK_MEETING: (jobId: string, candidateId: string) =>
    `${API_BASE_URL}/job/${jobId}/candidate/${candidateId}/meeting/mock`
};

// Candidate Endpoints
export const CANDIDATE_URLS = {
  GET_ALL: `${API_BASE_URL}/candidate`,
  CREATE: `${API_BASE_URL}/candidate`,
  GET_FROM_VETERANS_RESUME: `${API_BASE_URL}/candidate/veterans_resume`,
  CREATE_FROM_VETERANS_RESUME: `${API_BASE_URL}/candidate/veterans_resume`,
  GET_PROFILE: (candidateId: string) => `${API_BASE_URL}/candidate/${candidateId}`,
  GET_FIT_POSITIONS: `${API_BASE_URL}/candidate/fit_positions`,
  UPDATE_PROFILE: (candidateId: string) => `${API_BASE_URL}/candidate/${candidateId}`,
  DELETE: (candidateId: string) => `${API_BASE_URL}/candidate/${candidateId}`,
  GET_MATCH_JOBS: (candidateId: string) => `${API_BASE_URL}/candidate/${candidateId}/match_jobs`,
  GET_AVAILABLE_JOBS: (candidateId: string) =>
    `${API_BASE_URL}/candidate/${candidateId}/available_jobs`
};

// Screening Endpoints
export const SCREENING_URLS = {
  CREATE: `${API_BASE_URL}/screening`,
  CREATE_MOCK: `${API_BASE_URL}/screening/mock`,
  GET_ALL: `${API_BASE_URL}/screening`,
  GET_STATISTICS: `${API_BASE_URL}/screening/statistics`,
  GET: (screeningId: string) => `${API_BASE_URL}/screening/${screeningId}`,
  RESEND: (screeningId: string) => `${API_BASE_URL}/screening/${screeningId}/resend`,
  UPDATE: (screeningId: string) => `${API_BASE_URL}/screening/${screeningId}`,
  UPDATE_QUESTION_VIDEO_DURATION: (screeningId: string) =>
    `${API_BASE_URL}/screening/${screeningId}/question/video_duration`,
  CANCEL: (screeningId: string) => `${API_BASE_URL}/screening/${screeningId}`,
  REVIEW: (screeningId: string) => `${API_BASE_URL}/screening/${screeningId}/review`
};

// Question Bank Endpoints
export const QUESTION_BANK_URLS = {
  GET_ALL: `${API_BASE_URL}/question_bank`,
  CREATE: `${API_BASE_URL}/question_bank`,
  GET_CATEGORIES: `${API_BASE_URL}/question_bank/categories`,
  UPDATE_CATEGORIES: `${API_BASE_URL}/question_bank/categories`,
  EXTRACT: `${API_BASE_URL}/question_bank/extract`,
  GET: (questionId: string) => `${API_BASE_URL}/question_bank/${questionId}`,
  UPDATE: (questionId: string) => `${API_BASE_URL}/question_bank/${questionId}`,
  DELETE: (questionId: string) => `${API_BASE_URL}/question_bank/${questionId}`,
  UPDATE_ORDER: (questionId: string) => `${API_BASE_URL}/question_bank/${questionId}/order`
};

// Meeting Endpoints
export const MEETING_URLS = {
  GET_DASHBOARD: `${API_BASE_URL}/meeting`,
  GET_RECENT_MEETINGS: `${API_BASE_URL}/meeting/recent`,
  GET_UPCOMING_MEETINGS: `${API_BASE_URL}/meeting/upcoming`,

  GET: (meetingId: string) => `${API_BASE_URL}/meeting/${meetingId}`,
  GET_TRANSCRIPT: (meetingId: string) => `${API_BASE_URL}/meeting/${meetingId}/transcript`,
  BOOKMARK_TRANSCRIPT: (meetingId: string) =>
    `${API_BASE_URL}/meeting/${meetingId}/transcript/bookmark`,
  UPDATE_NOTE: (meetingId: string) => `${API_BASE_URL}/meeting/${meetingId}/note`,
  DELETE_MEETING: (meetingId: string) => `${API_BASE_URL}/meeting/${meetingId}`,
  UPDATE_MEETING: (meetingId: string) => `${API_BASE_URL}/meeting/${meetingId}`,
  UPDATE_MEETING_URL: (meetingId: string) => `${API_BASE_URL}/meeting/${meetingId}/url`,
  IMPORT_MOCK_TRANSCRIPT: (meetingId: string) =>
    `${API_BASE_URL}/meeting/${meetingId}/transcript/mock`
};

// Admin Endpoints
export const ADMIN_URLS = {
  LOGIN: `${API_BASE_URL}/admin/auth/login`,
  REFRESH_TOKEN: `${API_BASE_URL}/admin/auth/refresh`,
  GET_PROFILE: `${API_BASE_URL}/admin/profile`,
  OPENAI: {
    GET_ASSISTANT: (name: string) => `${API_BASE_URL}/admin/openai/assistant/${name}`,
    UPDATE_ASSISTANT: (name: string) => `${API_BASE_URL}/admin/openai/assistant/${name}`
  },
  EXTRACTOR: {
    EXTRACT_JD: `${API_BASE_URL}/admin/extractor/jd`,
    EXTRACT_RESUME: `${API_BASE_URL}/admin/extractor/resume`,
    EXTRACT_QUESTION_BANK: `${API_BASE_URL}/admin/extractor/question_bank`,
    EXTRACT_VETERANS_RESUME: `${API_BASE_URL}/admin/extractor/veterans_resume`
  },
  PROMPT: {
    GET: (key: string) => `${API_BASE_URL}/admin/prompt/${key}`,
    UPDATE: (key: string) => `${API_BASE_URL}/admin/prompt/${key}`
  },
  ANALYSIS_PROVIDER: {
    GET: `${API_BASE_URL}/admin/analysis_provider`,
    UPDATE: `${API_BASE_URL}/admin/analysis_provider`
  },
  ANALYZER: {
    JOB_REQUIREMENTS: `${API_BASE_URL}/admin/analyzer/jd`,
    RESUME: `${API_BASE_URL}/admin/analyzer/resume`,
    OVERVIEW: `${API_BASE_URL}/admin/analyzer/overview`,
    INTERVIEW: `${API_BASE_URL}/admin/analyzer/interview`
  },
  GENERATOR: {
    GENERATE_VETERANS_RESUME: `${API_BASE_URL}/admin/generator/veterans_resume`
  },
  MAILER: {
    SEND_BATCH_EMAIL: `${API_BASE_URL}/admin/mailer/batch`
  },
  USER: {
    GET_ALL: `${API_BASE_URL}/admin/user`,
    GET: (accountId: string) => `${API_BASE_URL}/admin/user/${accountId}`,
    CREATE: `${API_BASE_URL}/admin/user/create`,
    UPDATE_AGENCY_PLAN: `${API_BASE_URL}/admin/user/plan`,
    GENERATE_TOKEN: `${API_BASE_URL}/admin/user/token`
  },
  AGENCY: {
    GET_ALL: `${API_BASE_URL}/admin/agency`,
    UPDATE_CUSTOM_FEATURE: `${API_BASE_URL}/admin/agency/feature`
  },
  EMBEDDING: {
    TEST: `${API_BASE_URL}/admin/embedding/test`,
    MILITARY_TRANSLATION_SAMPLE: `${API_BASE_URL}/admin/embedding/military_translation_sample`
  }
};

// Currency Endpoints
export const CURRENCY_URLS = {
  GET: `${API_BASE_URL}/currency`
};

// Extractor Provider Endpoints
export const EXTRACTOR_PROVIDER_URLS = {
  GET: `${API_BASE_URL}/extractor_provider`
};

// Industry Endpoints
export const INDUSTRY_URLS = {
  GET: `${API_BASE_URL}/industry`
};

// Interview Endpoints
export const INTERVIEW_URLS = {
  GET_UNSCHEDULED_CANDIDATES: `${API_BASE_URL}/interview/unscheduled_candidates`
};

// Demo Endpoints
export const DEMO_URLS = {
  GET_EXTRACTOR_PROVIDERS: `${API_BASE_URL}/trial/extractor_provider`,
  EXTRACT_RESUME: `${API_BASE_URL}/trial/extract_resume`
};

// Candidate Screening Endpoints
export const CANDIDATE_SCREENING_URLS = {
  GET: `${API_BASE_URL}/candidate_screening`,
  START: `${API_BASE_URL}/candidate_screening/start`,
  SUBMIT: `${API_BASE_URL}/candidate_screening/answer`,
  COMPLETE: `${API_BASE_URL}/candidate_screening/complete`
};

// Job Board Endpoints
export const JOB_BOARD_URLS = {
  GET_INFO: (agency_id: string) => `${API_BASE_URL}/job_board/${agency_id}/info`,
  GET_ALL: (agency_id: string) => `${API_BASE_URL}/job_board/${agency_id}/jobs`,
  GET_JOB: ({ agency_id, job_id }: { agency_id: string; job_id: string }) =>
    `${API_BASE_URL}/job_board/${agency_id}/job/${job_id}`,
  APPLY_JOB: ({ agency_id, job_id }: { agency_id: string; job_id: string }) =>
    `${API_BASE_URL}/job_board/${agency_id}/job/${job_id}/apply`,
  APPLY_JOB_WITH_VETERANS_RESUME: ({ agency_id, job_id }: { agency_id: string; job_id: string }) =>
    `${API_BASE_URL}/job_board/${agency_id}/job/${job_id}/apply/veterans_resume`,
  TRANSLATE_VETERANS_RESUME: (agency_id: string) =>
    `${API_BASE_URL}/job_board/${agency_id}/veterans_resume`,
  CHECK_VETERANS_FEATURE_ENABLED: (agency_id: string) =>
    `${API_BASE_URL}/job_board/${agency_id}/veterans_resume/feature`
};

// Inbox Endpoints
export const INBOX_URLS = {
  GET_ALL: `${API_BASE_URL}/inbox`,
  UNREAD_COUNT: `${API_BASE_URL}/inbox/unread_count`,
  MARK_ALL_READ: `${API_BASE_URL}/inbox/mark_all_read`,
  MARK_AS_READ: (inboxId: string) => `${API_BASE_URL}/inbox/${inboxId}`,
  DELETE: (inboxId: string) => `${API_BASE_URL}/inbox/${inboxId}`
};

// Application Questions Endpoints
export const APPLICATION_QUESTIONS_URLS = {
  GET: `${API_BASE_URL}/application_questions`,
  SUBMIT: `${API_BASE_URL}/application_questions/submit`
};
